/**
 * Service Factures (Invoices) - API Tracking
 * Endpoints principaux:
 * - GET /api/users/invoices/           (factures de tous les élèves du parent)
 * - GET /api/users/students/{id}/invoices/ (factures d'un élève spécifique)
 * - POST /api/invoices/{invoice_id}/pay/
 * - GET  /api/invoices/{invoice_id}/payments/
 * - POST /api/invoices/bulk-pay/
 */
import { createTrackingClient } from "../trackingApi";

function getClient(accessToken: string | undefined) {
  return createTrackingClient(accessToken);
}

export interface Invoice {
  id: string;
  domain: string;
  domain_display?: string;
  entity_id: string;
  entity_type?: string;
  entity_name?: string;
  entity_user_id?: number | null;
  amount: number | string;
  remaining_amount?: number | string;
  due_date: string;
  status: string;
  status_display?: string;
  reference: string;
  description?: string | null;
  period_start?: string | null;
  period_end?: string | null;
}

export interface InvoiceListResponse {
  count: number;
  next: number | null;
  previous: number | null;
  results: Invoice[];
}

export interface InvoicePaymentCreatePayload {
  amount: number | string;
  method: string;
  provider?: string | null;
  transaction_id?: string | null;
  phone_number?: string | null;
  notes?: string | null;
}

export async function fetchParentInvoices(
  accessToken: string | undefined,
  params?: { status?: string; domain?: string; page?: number; page_size?: number }
): Promise<InvoiceListResponse> {
  const client = getClient(accessToken);
  const { data } = await client.get<InvoiceListResponse>("/api/users/invoices/", {
    params,
  });
  return {
    count: data?.count ?? 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    results: Array.isArray(data?.results) ? data.results : [],
  };
}

/** Payer une facture (POST /api/invoices/{invoice_id}/pay/) */
export async function payInvoice(
  accessToken: string | undefined,
  invoiceId: string,
  payload: InvoicePaymentCreatePayload
): Promise<{ payment: any; invoice: Invoice; message: string }> {
  const client = getClient(accessToken);
  const { data } = await client.post<{ payment: any; invoice: Invoice; message: string }>(
    `/api/invoices/${invoiceId}/pay/`,
    payload
  );
  return data;
}

export interface BulkPayPayload {
  invoice_ids: string[];
  amount: number | string;
  method: string;
  provider?: string | null;
  transaction_id?: string | null;
  phone_number?: string | null;
  notes?: string | null;
}

/** Paiement groupé (POST /api/invoices/bulk-pay/) */
export async function bulkPayInvoices(
  accessToken: string | undefined,
  payload: BulkPayPayload
): Promise<{
  payments: any[];
  invoices: Invoice[];
  total_distributed: number;
  message: string;
}> {
  const client = getClient(accessToken);
  const { data } = await client.post<{
    payments: any[];
    invoices: Invoice[];
    total_distributed: number;
    message: string;
  }>("/api/invoices/bulk-pay/", payload);
  return data;
}

